import { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Plus, X, Check, ExternalLink } from 'lucide-react';
import type { Collection } from '../types';

interface CollectionChipProps {
  collection: Collection;
  active: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onOpenAll?: () => void;
}

// A collection chip doubles as a drop target — drag a technique onto it to add.
function CollectionChip({ collection, active, onSelect, onDelete, onOpenAll }: CollectionChipProps) {
  const { setNodeRef, isOver } = useDroppable({ id: `collection-${collection.id}`, data: { collectionId: collection.id } });
  return (
    <span
      ref={setNodeRef}
      className={`collection-chip${active ? ' collection-chip--active' : ''}${isOver ? ' collection-chip--over' : ''}`}
    >
      <button type="button" className="collection-chip-main" onClick={onSelect}>
        {collection.name}
        <span className="collection-chip-count">{collection.techniqueIds.length}</span>
      </button>
      {onOpenAll && collection.techniqueIds.length > 0 && (
        <button type="button" className="collection-chip-playall" onClick={(e) => { e.stopPropagation(); onOpenAll(); }} aria-label={`Open all ${collection.name} videos in picture-in-picture`} title="Open all videos">
          <ExternalLink size={12} />
        </button>
      )}
      {active && (
        <button type="button" className="collection-chip-del" onClick={onDelete} aria-label={`Delete ${collection.name}`} title="Delete collection">
          <X size={12} />
        </button>
      )}
    </span>
  );
}

interface CollectionChipsProps {
  collections: Collection[];
  activeId: string | null;
  onSelect: (id: string | null) => void;
  onCreate: (name: string) => void;
  onDelete: (id: string) => void;
  /** Desktop-only: open every video in a collection as a grid of PiP players. */
  onOpenAll?: (id: string) => void;
}

export default function CollectionChips({ collections, activeId, onSelect, onCreate, onDelete, onOpenAll }: CollectionChipsProps) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');

  const commit = () => {
    const trimmed = name.trim();
    if (trimmed) onCreate(trimmed);
    setName('');
    setCreating(false);
  };

  return (
    <div className="collection-chips">
      {collections.length > 0 && (
        <button
          type="button"
          className={`collection-chip collection-chip--all${activeId === null ? ' collection-chip--active' : ''}`}
          onClick={() => onSelect(null)}
        >
          All
        </button>
      )}

      {collections.map((c) => (
        <CollectionChip
          key={c.id}
          collection={c}
          active={activeId === c.id}
          onSelect={() => onSelect(activeId === c.id ? null : c.id)}
          onDelete={() => onDelete(c.id)}
          onOpenAll={onOpenAll ? () => onOpenAll(c.id) : undefined}
        />
      ))}

      {creating ? (
        <span className="collection-chip collection-chip--new">
          <input
            className="collection-chip-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setName(''); setCreating(false); } }}
            placeholder="Name…"
            autoFocus
          />
          <button type="button" className="collection-chip-del" onClick={commit} aria-label="Create collection">
            <Check size={13} />
          </button>
        </span>
      ) : (
        <button type="button" className="collection-chip collection-chip--add" onClick={() => setCreating(true)} aria-label="Create collection">
          <Plus size={14} /> Create Collection
        </button>
      )}
    </div>
  );
}
